import { z } from 'zod';
import { AgentProviderSpec } from './llm-providers.js';
/**
 * Claim checks (task 62, decision 0040). An insurer sets checks on a question in the studio; once the
 * question is answered, CyberScore checks the answer against the business's own documents:
 *
 *  - evidence: is the answer backed by an uploaded document (any, or of the types listed)?
 *  - policy: does the business's policy say what the insurer requires (`statement`)?
 *  - document_field: a field read from a document of one type, e.g. that a certificate hasn't expired.
 *
 * The first two are read by the checker agent from the passages hybrid retrieval finds; the third is
 * worked out from the extraction (task 34), with no model. Each gives a verdict. A supported answer counts
 * as read from a document in the score; a contradicted one goes to review; anything else stays as declared.
 */
export const CLAIM_CHECK_KINDS = ['evidence', 'policy', 'document_field'];
export const DOCUMENT_FIELD_RULES = ['not_expired', 'present', 'matches_answer'];
const DocType = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/, 'Pick a document type');
export const ClaimCheckDef = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('evidence'),
        /** Only documents of these types count. Empty: any document of the assessment. */
        document_types: z.array(DocType).max(10).default([]),
    }),
    z.strictObject({
        kind: z.literal('policy'),
        document_types: z.array(DocType).max(10).default([]),
        /** What the business's documents must say, e.g. "MFA is required for every email account". */
        statement: z.string().trim().min(3, 'Say what the policy must say').max(500),
    }),
    z.strictObject({
        kind: z.literal('document_field'),
        document_type: DocType,
        field: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Pick a field'),
        rule: z.enum(DOCUMENT_FIELD_RULES),
    }),
]);
export const ClaimChecks = z.array(ClaimCheckDef).max(5);
export const CLAIM_CHECK_KIND_LABEL = {
    evidence: 'Backed by a document',
    policy: 'Policy says',
    document_field: 'Document field',
};
export const DOCUMENT_FIELD_RULE_LABEL = {
    not_expired: 'has not passed',
    present: 'is there',
    matches_answer: 'matches the answer',
};
/** A check in a sentence, for editors and reviewers. */
export function describeCheck(c, typeName = (k) => k) {
    const types = (ts) => (ts.length ? ` (${ts.map(typeName).join(', ')})` : '');
    switch (c.kind) {
        case 'evidence':
            return `The answer is backed by a document${types(c.document_types)}`;
        case 'policy':
            return `A policy${types(c.document_types)} says: “${c.statement}”`;
        case 'document_field':
            return `${typeName(c.document_type)}: ${c.field.replace(/_/g, ' ')} ${DOCUMENT_FIELD_RULE_LABEL[c.rule]}`;
    }
}
// —— verdicts ——
export const CLAIM_VERDICTS = ['supported', 'not_supported', 'contradicted', 'cant_tell'];
export const CLAIM_VERDICT_LABEL = {
    supported: 'Supported',
    not_supported: 'Not supported',
    contradicted: 'Contradicted',
    cant_tell: 'Can’t tell',
};
export const CLAIM_VERDICT_TONE = {
    supported: 'good',
    not_supported: 'warn',
    contradicted: 'bad',
    cant_tell: 'neutral',
};
/** Verdicts an underwriter should look at: the answer isn't backed, or a document says otherwise. */
export const claimFails = (v) => v === 'contradicted' || v === 'not_supported';
/**
 * The verdict a question's checks add up to: any contradiction wins, then anything not backed, then
 * "can't tell"; supported only when every check is. Null without checks.
 */
export function overallVerdict(vs) {
    if (!vs.length)
        return null;
    for (const v of ['contradicted', 'not_supported', 'cant_tell'])
        if (vs.includes(v))
            return v;
    return 'supported';
}
// —— the agent (POST /v1/claims/check on the agent service, `CheckRequest` in Python) ——
export const AgentPassage = z.strictObject({
    id: z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/),
    document_id: z.string().min(1).max(64),
    file_name: z.string().min(1).max(200),
    page: z.number().int().positive(),
    text: z.string().min(1).max(4000),
});
export const AgentClaim = z.strictObject({
    id: z.string().regex(/^[A-Za-z0-9_.:-]{1,80}$/),
    kind: z.enum(['evidence', 'policy']),
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(500),
    statement: z.string().max(500).default(''),
    passages: z.array(AgentPassage).max(8).default([]),
});
export const ClaimCheckRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    claims: z.array(AgentClaim).min(1).max(20),
    today: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .default(null),
    provider: AgentProviderSpec.nullable().optional(),
});
/** The value of a `claim:<id>` proposal. */
export const AgentClaimValue = z.object({ verdict: z.enum(CLAIM_VERDICTS), reason: z.string() });
// —— document fields, worked out without a model ——
const isoDate = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
const same = (a, b) => {
    if (typeof a === 'number' || typeof b === 'number')
        return Number(a) === Number(b);
    if (typeof a === 'boolean' || typeof b === 'boolean')
        return String(a).toLowerCase().replace(/^yes$/, 'true').replace(/^no$/, 'false') === String(b).toLowerCase().replace(/^yes$/, 'true').replace(/^no$/, 'false'); // prettier-ignore
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};
/**
 * A document_field check from the newest value read for that field (null: no document of the type has
 * it). `today` is YYYY-MM-DD in Malaysia.
 */
export function fieldVerdict(rule, value, answer, today) {
    if (value === null || value === undefined || value === '')
        return { verdict: 'not_supported', reason: 'No document of this type gives this field.' };
    switch (rule) {
        case 'not_expired': {
            const d = isoDate(value);
            if (!d)
                return { verdict: 'cant_tell', reason: `The document’s value (${String(value)}) isn’t a date.` };
            return d >= today
                ? { verdict: 'supported', reason: `Valid until ${d}.` }
                : { verdict: 'contradicted', reason: `It passed on ${d}.` };
        }
        case 'present':
            return value === false
                ? { verdict: 'contradicted', reason: 'The document says no.' }
                : { verdict: 'supported', reason: 'The document gives it.' };
        case 'matches_answer': {
            const a = Array.isArray(answer) ? answer.join(', ') : answer;
            return same(value, a)
                ? { verdict: 'supported', reason: 'The document gives the same value.' }
                : { verdict: 'contradicted', reason: `The document says ${String(value)}.` };
        }
    }
}
//# sourceMappingURL=claims.js.map